import React, { useState, useRef, useEffect, useCallback, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { useIntersectionObserver } from '@/utils/animations';

// Interfaces for type safety
interface GalleryImage {
  id: number;
  src: string;
  alt: string;
  thumb: string;
}

interface LazyImageProps {
  src: string;
  thumb: string;
  alt: string;
  className?: string;
  onClick?: () => void;
}

interface GalleryThumbnailProps {
  image: GalleryImage;
  index: number;
  onClick: (index: number) => void;
}

// Gallery images
const galleryImages: GalleryImage[] = [
  { id: 1, src: '/lovable-uploads/1.jpg', alt: 'Emi y Carlos', thumb: '/lovable-uploads/thumbs/1.jpg' },
  { id: 2, src: '/lovable-uploads/2.jpg', alt: 'Emi y Carlos', thumb: '/lovable-uploads/thumbs/2.jpg' },
  { id: 3, src: '/lovable-uploads/3.jpg', alt: 'Emi y Carlos', thumb: '/lovable-uploads/thumbs/3.jpg' },
  { id: 4, src: '/lovable-uploads/4.jpg', alt: 'Emi y Carlos', thumb: '/lovable-uploads/thumbs/4.jpg' },
  { id: 5, src: '/lovable-uploads/5.jpg', alt: 'Emi y Carlos', thumb: '/lovable-uploads/thumbs/5.jpg' },
  { id: 6, src: '/lovable-uploads/6.jpg', alt: 'Emi y Carlos', thumb: '/lovable-uploads/thumbs/6.jpg' },
  { id: 8, src: '/lovable-uploads/8.jpg', alt: 'Emi y Carlos', thumb: '/lovable-uploads/thumbs/8.jpg' },
  { id: 9, src: '/lovable-uploads/fotoemi7.JPG', alt: 'Emi y Carlos', thumb: '/lovable-uploads/thumbs/fotoemi7.jpg' },
  { id: 10, src: '/lovable-uploads/fotoemi1.JPG', alt: 'Emi y Carlos', thumb: '/lovable-uploads/thumbs/fotoemi1.jpg' },
  { id: 11, src: '/lovable-uploads/fotoemi6.JPG', alt: 'Emi y Carlos', thumb: '/lovable-uploads/thumbs/fotoemi6.jpg' },
  { id: 12, src: '/lovable-uploads/fotoemi5.JPG', alt: 'Emi y Carlos', thumb: '/lovable-uploads/thumbs/fotoemi5.jpg' },
  ];

// Using a much smaller image as placeholder
const placeholderImage = '/lovable-uploads/placeholder.svg';

// Memoized LazyImage component with further optimizations
const LazyImage = memo<LazyImageProps>(({ src, thumb, alt, className = '', onClick }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [showFullImage, setShowFullImage] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(placeholderImage);
  const imageRef = useRef<HTMLDivElement>(null);
  const hasAttemptedToLoad = useRef(false);

  useEffect(() => {
    // Avoid trying to load the image multiple times
    if (hasAttemptedToLoad.current) return;
    
    // Use Intersection Observer to load images only when they come into view
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          hasAttemptedToLoad.current = true;
          // Primero cargar la miniatura
          if (thumb) {
            setCurrentSrc(thumb);
            
            // Luego precargar la imagen completa con retraso para no bloquear el hilo principal
            setTimeout(() => {
              const fullImg = new Image();
              fullImg.src = src;
              fullImg.onload = () => {
                // Usar requestAnimationFrame para sincronizar con el ciclo de pintado del navegador
                window.requestAnimationFrame(() => {
                  setShowFullImage(true);
            setIsLoaded(true);
                });
          };
            }, 300); // Pequeño retraso para dar tiempo al navegador a renderizar otras cosas
          }
          observer.unobserve(entry.target);
        }
      });
    }, {
      rootMargin: '300px', // Aumentar el margen para cargar antes
      threshold: 0.1
    });

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
    };
  }, [src, thumb]);

  // Simplificar el markup para mejorar el rendimiento
  return (
    <div ref={imageRef} className={`relative ${className} will-change-transform`}>
      <img
        src={showFullImage ? src : currentSrc}
        alt={alt}
        onClick={onClick}
        className={`w-full h-full object-cover ${isLoaded ? 'opacity-100' : 'opacity-70'}`}
        loading="lazy"
        decoding="async"
        style={{ 
          willChange: 'transform, opacity',
          // Evitar reflow al cargar la imagen
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden'
        }}
      />
      {!isLoaded && (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-sage/30 border-t-sage rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
});

LazyImage.displayName = 'LazyImage';

// Memoized thumbnail component
const GalleryThumbnail = memo<GalleryThumbnailProps>(({ image, index, onClick }) => {
  return (
    <div 
      className="flex-shrink-0 snap-center cursor-pointer w-[240px] md:w-[280px] rounded-xl overflow-hidden transform-gpu"
      onClick={() => onClick(index)}
      style={{ 
        contentVisibility: 'auto',
        containIntrinsicSize: '280px 400px' // Aproximadamente la proporción 3:4
      }}
    >
      <AspectRatio ratio={3/4}>
        <LazyImage
          src={image.src}
          thumb={image.thumb}
          alt={image.alt}
          className="w-full h-full"
          onClick={() => onClick(index)}
        />
      </AspectRatio>
    </div>
  );
});

GalleryThumbnail.displayName = 'GalleryThumbnail';

// Componente modal separado para simplificar la lógica principal
const ImageModal = memo(({ 
  selectedImage, 
  onClose, 
  onPrev, 
  onNext, 
  images, 
  hasNext, 
  hasPrev 
}: { 
  selectedImage: number, 
  onClose: () => void, 
  onPrev: () => void, 
  onNext: () => void, 
  images: GalleryImage[],
  hasNext: boolean,
  hasPrev: boolean
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' && hasNext) onNext();
      else if (e.key === 'ArrowLeft' && hasPrev) onPrev();
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
      document.addEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [onClose, onNext, onPrev, hasNext, hasPrev]);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div 
        ref={modalRef}
        className="relative max-w-4xl w-full flex justify-center items-center px-4"
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute -top-10 right-0 text-white/90 hover:text-white transition-colors z-50 p-2"
          aria-label="Cerrar galería"
        >
          <X size={24} />
        </button>
        
        {/* Modal image */}
        <div className="overflow-hidden bg-transparent rounded-md">
          <img
            key={`modal-image-${selectedImage}`}
            src={images[selectedImage].src}
            alt={images[selectedImage].alt}
            className="w-full h-full object-contain max-h-[80vh]"
            loading="eager"
          />
        </div>
        
        {/* Navigation arrows */}
        {hasPrev && (
          <button
            onClick={onPrev}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 md:-translate-x-16 text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-2 transition-all duration-300"
            aria-label="Imagen anterior"
          >
            <ChevronLeft size={24} />
          </button>
        )}
        
        {hasNext && (
          <button
            onClick={onNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 md:translate-x-16 text-white/80 hover:text-white bg-black/30 hover:bg-black/50 rounded-full p-2 transition-all duration-300"
            aria-label="Imagen siguiente"
          >
            <ChevronRight size={24} />
          </button>
        )}
      </div>
    </div>
  );
});

ImageModal.displayName = 'ImageModal';

const Gallery: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const [isPaused, setIsPaused] = useState(false);
  const [isGalleryVisible, setIsGalleryVisible] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const carouselRef = useRef<HTMLDivElement | null>(null);
  const { ref: sectionRef, isVisible } = useIntersectionObserver();
  
  // Establecer la visibilidad de la galería cuando entra en la vista
  useEffect(() => {
    if (isVisible && !isGalleryVisible) {
      setIsGalleryVisible(true);
    }
  }, [isVisible, isGalleryVisible]);

  // Memoized handlers
  const handleSelectImage = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);
  
  const handleCloseModal = useCallback(() => {
    setSelectedImage(null);
  }, []);
  
  const handlePrevImage = useCallback(() => {
    setSelectedImage(prev => 
      prev !== null && prev > 0 ? prev - 1 : prev
    );
  }, []);
  
  const handleNextImage = useCallback(() => {
    setSelectedImage(prev => 
      prev !== null && prev < galleryImages.length - 1 ? prev + 1 : prev
    );
  }, []);

  // Auto scroll carousel functionality - optimized for performance
  useEffect(() => {
    let intervalId: NodeJS.Timeout | null = null;
    
    if (isGalleryVisible && !isPaused && selectedImage === null && !isScrolling) {
      intervalId = setInterval(() => {
        if (carouselRef.current) {
          const scrollAmount = 320;
          const currentScroll = carouselRef.current.scrollLeft;
          const maxScroll = carouselRef.current.scrollWidth - carouselRef.current.clientWidth;
          
          // Prevent jank by using requestAnimationFrame
          window.requestAnimationFrame(() => {
            setIsScrolling(true);
            if (currentScroll >= maxScroll - 10) {
              carouselRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
              carouselRef.current?.scrollTo({ left: currentScroll + scrollAmount, behavior: 'smooth' });
            }
            
            // Clear the scrolling flag after animation completes
            setTimeout(() => setIsScrolling(false), 500);
          });
        }
      }, 5000); // Mayor intervalo para reducir el procesamiento
    }
    
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isGalleryVisible, isPaused, selectedImage, isScrolling]);

  // Scroll carousel navigation functions - memoized with useCallback
  const scrollCarousel = useCallback((direction: 'left' | 'right') => {
    if (!carouselRef.current || isScrolling) return;
    
    setIsScrolling(true);
    
    const scrollAmount = 320; // Approximate width of a card + gap
    const currentScroll = carouselRef.current.scrollLeft;
    const maxScroll = carouselRef.current.scrollWidth - carouselRef.current.clientWidth;
    
    // Use requestAnimationFrame for smooth scrolling
    window.requestAnimationFrame(() => {
    // Si llegamos al final, reiniciar al principio
    if (direction === 'right' && currentScroll >= maxScroll - 10) {
        carouselRef.current?.scrollTo({
        left: 0,
        behavior: 'smooth'
      });
    } else {
        carouselRef.current?.scrollTo({
        left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
        behavior: 'smooth'
      });
    }
      
      // Clear the scrolling flag after animation completes
      setTimeout(() => setIsScrolling(false), 500);
    });
  }, [isScrolling]);

  // Manejar eventos de scroll para mejorar la experiencia
  useEffect(() => {
    const carousel = carouselRef.current;
    
    const handleScroll = () => {
      setIsScrolling(true);
      // Debounce the scroll end detection
      clearTimeout((window as any).scrollEndTimer);
      (window as any).scrollEndTimer = setTimeout(() => {
        setIsScrolling(false);
      }, 200);
    };
    
    if (carousel) {
      carousel.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      if (carousel) {
        carousel.removeEventListener('scroll', handleScroll);
      }
      clearTimeout((window as any).scrollEndTimer);
    };
  }, []);

  if (!isGalleryVisible) {
    // Renderizar un marcador de posición ligero cuando la galería no está visible
    return (
      <section id="galeria" className="py-20 px-4 bg-cream" ref={sectionRef as React.RefObject<HTMLDivElement>}>
        <div className="w-full h-[300px]" /> {/* Reservar espacio */}
      </section>
    );
  }

  return (
    <section 
      id="galeria" 
      className="py-20 px-4 bg-cream"
      ref={sectionRef as React.RefObject<HTMLDivElement>}
    >
      <div className="max-w-6xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-parisienne text-forest mb-4">
          Galería de fotos
        </h2>
            
        <div className="w-24 h-0.5 bg-sage/30 mx-auto mb-12"></div>
        
        {/* Carousel Container */}
        <div 
          className="relative overflow-hidden"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Navigation Arrows */}
          <button 
            onClick={() => scrollCarousel('left')}
            className="absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-sm text-forest/70 hover:text-forest transition-all"
            aria-label="Previous images"
            disabled={isScrolling}
          >
            <ChevronLeft size={24} />
          </button>
          
          <button 
            onClick={() => scrollCarousel('right')}
            className="absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white rounded-full p-2 shadow-sm text-forest/70 hover:text-forest transition-all"
            aria-label="Next images"
            disabled={isScrolling}
          >
            <ChevronRight size={24} />
          </button>
          
          {/* Horizontal Scrolling Carousel - Optimizado */}
          <div 
            ref={carouselRef}
            className="flex overflow-x-auto scrollbar-hide snap-x snap-mandatory gap-6 pb-6 -mx-4 px-4"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              scrollBehavior: 'smooth',
              WebkitOverflowScrolling: 'touch' // Mejorar scroll en iOS
            }}
          >
            {galleryImages.map((image, index) => (
              <GalleryThumbnail 
                key={image.id}
                image={image} 
                index={index} 
                onClick={handleSelectImage}
                    />
            ))}
          </div>
        </div>

        {/* Gallery Modal - Separado en componente */}
          {selectedImage !== null && (
          <ImageModal
            selectedImage={selectedImage}
            onClose={handleCloseModal}
            onPrev={handlePrevImage}
            onNext={handleNextImage}
            images={galleryImages}
            hasPrev={selectedImage > 0}
            hasNext={selectedImage < galleryImages.length - 1}
          />
                )}
      </div>
    </section>
  );
};

export default memo(Gallery);
